import { useCallback, useMemo } from "react";

import { Skeleton } from "@heroui/react";
import lodash from "lodash";
import { useLocalStorage } from "usehooks-ts";

import {
	type AvailableLanguages,
	DEFAULT_LANGUAGE,
	DEFAULT_TRANSLATIONS,
	LANG_MAPPER,
	LS_LANGUAGE_KEY,
} from "@onesolution/constants";
import useIsMounted from "./useIsMounted";

export type TradOptions = {
	capitalize?: boolean;
	skeleton?: boolean;
};
const DEFAULT_OPTIONS: TradOptions = {
	capitalize: true,
	skeleton: true,
};

import type enjson from "../translations/en.json";
type TranslationsObject = typeof enjson;
type DeepKeys<T> = T extends object
	? {
			[K in keyof T]: K extends string
				? T[K] extends object
					? `${K}` | `${K}.${DeepKeys<T[K]>}`
					: `${K}`
				: never;
		}[keyof T]
	: never;

export type TranslationsKeys = DeepKeys<TranslationsObject>;

export default function useLanguage(defaultTradOption?: TradOptions) {
	const isMounted = useIsMounted();
	const [savedLang, setSavedLang] = useLocalStorage(
		LS_LANGUAGE_KEY,
		DEFAULT_LANGUAGE,
	);
	const lang = savedLang || DEFAULT_LANGUAGE;
	const translations = useMemo<(typeof LANG_MAPPER)[AvailableLanguages]>(() => {
		return LANG_MAPPER[lang];
	}, [lang]);

	const setLanguage = useCallback(
		(toSet: AvailableLanguages) => {
			setSavedLang(toSet);
		},
		[setSavedLang],
	);

	const t = useCallback(
		(key: TranslationsKeys, options?: TradOptions) => {
			const opts = { ...DEFAULT_OPTIONS, ...defaultTradOption, ...options };

			if (!isMounted)
				return opts.skeleton ? (
					<Skeleton className="w-1/3 h-4 rounded-lg" />
				) : null;

			let out =
				lodash.get(translations, key as string) ||
				lodash.get(DEFAULT_TRANSLATIONS, key as string);

			if (opts?.capitalize) out = lodash.capitalize(out);

			return out;
		},
		[defaultTradOption, translations, isMounted],
	);

	return {
		lang: !isMounted ? null : lang,
		locale: `${lang}-${lang.toUpperCase()}`,
		setLanguage,
		t,
	};
}
